import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ShoppingCart, Menu, X, ChevronDown, Heart, Sparkles } from 'lucide-react';
import { useShopify } from '../context/ShopifyContext';

const Header = ({ cartCount = 0 }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [shopMenuOpen, setShopMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/collections/all?search=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white shadow-sm'
    }`}>
      {/* Announcement Bar */}
      <motion.div 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className="bg-gradient-to-r from-pink-500 via-rose-500 to-pink-500 py-2.5 px-4 text-center overflow-hidden relative"
      >
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{ left: `${i * 18 + 5}%`, top: '20%' }}
              animate={{
                scale: [0, 1, 0],
                opacity: [0, 0.6, 0],
                rotate: [0, 180, 360],
              }}
              transition={{
                duration: 3,
                delay: i * 0.5,
                repeat: Infinity,
              }}
            >
              <Sparkles size={10} className="text-white/40" />
            </motion.div>
          ))}
        </div>
        <p className="text-white text-sm font-medium relative z-10 flex items-center justify-center gap-2">
          <motion.span
            animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Sparkles size={14} className="text-pink-200" />
          </motion.span>
          Free Shipping on Orders Over $50 | Shop Our Pink Collection Today →
          <motion.span
            animate={{ rotate: [0, -15, 15, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
          >
            <Sparkles size={14} className="text-pink-200" />
          </motion.span>
        </p>
      </motion.div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-3">
          {/* Mobile Menu Button */}
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-pink-900 hover:bg-pink-50 rounded-xl transition-colors"
          >
            <AnimatePresence mode="wait">
              {mobileMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                >
                  <X size={24} />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                >
                  <Menu size={24} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>

          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <motion.div 
              whileHover={{ scale: 1.05 }}
              className="text-2xl md:text-3xl font-bold" 
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-pink-600 bg-clip-text text-transparent">
                The Pink Edit
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <div 
              className="relative group"
              onMouseEnter={() => setShopMenuOpen(true)}
              onMouseLeave={() => setShopMenuOpen(false)}
            >
              <button className="flex items-center space-x-1 text-gray-700 hover:text-pink-600 transition-colors font-medium py-2">
                <span>Shop</span>
                <motion.div animate={{ rotate: shopMenuOpen ? 180 : 0 }}>
                  <ChevronDown size={16} />
                </motion.div>
              </button>
              
              {/* Mega Menu */}
              <AnimatePresence>
                {shopMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-1/2 transform -translate-x-1/2 mt-0 w-screen max-w-3xl"
                  >
                    <div className="bg-white rounded-2xl shadow-2xl border border-pink-100 p-8 mt-2" onClick={() => setShopMenuOpen(false)}>
                      <div className="grid grid-cols-3 gap-8">
                        <div>
                          <h3 className="text-sm font-bold text-pink-900 mb-4 uppercase tracking-wider">Clothing</h3>
                          <ul className="space-y-3">
                            <li>
                              <Link to="/collections/dresses" onClick={() => setShopMenuOpen(false)} className="text-gray-600 hover:text-pink-600 transition-colors flex items-center gap-2 group">
                                <span className="w-1 h-1 bg-pink-300 rounded-full group-hover:bg-pink-500 transition-colors" />
                                Dresses
                              </Link>
                            </li>
                            <li>
                              <Link to="/collections/tops" onClick={() => setShopMenuOpen(false)} className="text-gray-600 hover:text-pink-600 transition-colors flex items-center gap-2 group">
                                <span className="w-1 h-1 bg-pink-300 rounded-full group-hover:bg-pink-500 transition-colors" />
                                Tops & Bodysuits
                              </Link>
                            </li>
                            <li>
                              <Link to="/collections/sets" onClick={() => setShopMenuOpen(false)} className="text-gray-600 hover:text-pink-600 transition-colors flex items-center gap-2 group">
                                <span className="w-1 h-1 bg-pink-300 rounded-full group-hover:bg-pink-500 transition-colors" />
                                Sets & Loungewear
                              </Link>
                            </li>
                            <li>
                              <Link to="/collections/shorts" onClick={() => setShopMenuOpen(false)} className="text-gray-600 hover:text-pink-600 transition-colors flex items-center gap-2 group">
                                <span className="w-1 h-1 bg-pink-300 rounded-full group-hover:bg-pink-500 transition-colors" />
                                Shorts & Jeans
                              </Link>
                            </li>
                          </ul>
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-pink-900 mb-4 uppercase tracking-wider">Accessories</h3>
                          <ul className="space-y-3">
                            <li>
                              <Link to="/collections/sunglasses" onClick={() => setShopMenuOpen(false)} className="text-gray-600 hover:text-pink-600 transition-colors flex items-center gap-2 group">
                                <span className="w-1 h-1 bg-pink-300 rounded-full group-hover:bg-pink-500 transition-colors" />
                                Sunglasses
                              </Link>
                            </li>
                            <li>
                              <Link to="/collections/jewelry" onClick={() => setShopMenuOpen(false)} className="text-gray-600 hover:text-pink-600 transition-colors flex items-center gap-2 group">
                                <span className="w-1 h-1 bg-pink-300 rounded-full group-hover:bg-pink-500 transition-colors" />
                                Jewelry
                              </Link>
                            </li>
                            <li>
                              <Link to="/collections/necklaces" onClick={() => setShopMenuOpen(false)} className="text-gray-600 hover:text-pink-600 transition-colors flex items-center gap-2 group">
                                <span className="w-1 h-1 bg-pink-300 rounded-full group-hover:bg-pink-500 transition-colors" />
                                Necklaces
                              </Link>
                            </li>
                            <li>
                              <Link to="/collections/bracelets" onClick={() => setShopMenuOpen(false)} className="text-gray-600 hover:text-pink-600 transition-colors flex items-center gap-2 group">
                                <span className="w-1 h-1 bg-pink-300 rounded-full group-hover:bg-pink-500 transition-colors" />
                                Bracelets
                              </Link>
                            </li>
                          </ul>
                        </div>
                        <div>
                          <h3 className="text-sm font-bold text-pink-900 mb-4 uppercase tracking-wider">Browse</h3>
                          <ul className="space-y-3">
                            <li>
                              <Link to="/collections/all" onClick={() => setShopMenuOpen(false)} className="text-pink-600 hover:text-pink-700 font-semibold flex items-center gap-2">
                                Shop All Products →
                              </Link>
                            </li>
                          </ul>
                          
                          {/* Featured Image */}
                          <div className="mt-6 rounded-xl overflow-hidden">
                            <Link to="/collections/all">
                              <img 
                                src="https://cdn.shopify.com/s/files/1/0808/2476/2588/files/IMG-7371.png" 
                                alt="Featured"
                                className="w-full h-32 object-cover hover:scale-105 transition-transform duration-500"
                              />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            
            <Link to="/about" className="text-gray-700 hover:text-pink-600 transition-colors font-medium">About</Link>
            <Link to="/contact" className="text-gray-700 hover:text-pink-600 transition-colors font-medium">Contact</Link>
          </nav>

          {/* Right Icons */}
          <div className="flex items-center space-x-2">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2.5 text-gray-700 hover:text-pink-600 hover:bg-pink-50 rounded-xl transition-all"
            >
              <Search size={22} />
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="hidden sm:flex p-2.5 text-gray-700 hover:text-pink-600 hover:bg-pink-50 rounded-xl transition-all"
            >
              <Heart size={22} />
            </motion.button>
            
            <Link to="/cart">
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="p-2.5 text-gray-700 hover:text-pink-600 hover:bg-pink-50 rounded-xl transition-all relative"
              >
                <ShoppingCart size={22} />
                <AnimatePresence>
                  {cartCount > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                      className="absolute -top-0.5 -right-0.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center font-bold shadow-lg"
                    >
                      {cartCount > 99 ? '99+' : cartCount}
                    </motion.span>
                  )}
                </AnimatePresence>
              </motion.div>
            </Link>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-pink-100 bg-gradient-to-b from-pink-50 to-white overflow-hidden"
          >
            <div className="py-6 px-4">
              <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search for products..."
                    className="w-full px-5 py-4 pl-14 rounded-2xl border-2 border-pink-200 focus:outline-none focus:ring-2 focus:ring-pink-300 focus:border-pink-400 text-lg shadow-inner"
                    autoFocus
                  />
                  <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 text-pink-400" size={24} />
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 px-6 py-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-medium"
                  >
                    Search
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden border-t border-pink-100 bg-white overflow-hidden"
          >
            <nav className="px-4 py-6 space-y-2">
              <Link 
                to="/collections/all" 
                className="block text-gray-900 hover:text-pink-600 font-bold py-3 px-4 rounded-xl hover:bg-pink-50 transition-colors" 
                onClick={() => setMobileMenuOpen(false)}
              >
                Shop All
              </Link>
              
              <div className="border-t border-pink-100 pt-4 mt-4">
                <p className="text-sm font-bold text-pink-900 mb-3 px-4 uppercase tracking-wider">Categories</p>
                <Link to="/collections/dresses" className="block text-gray-600 hover:text-pink-600 py-2 px-4 rounded-lg hover:bg-pink-50 transition-colors" onClick={() => setMobileMenuOpen(false)}>Dresses</Link>
                <Link to="/collections/tops" className="block text-gray-600 hover:text-pink-600 py-2 px-4 rounded-lg hover:bg-pink-50 transition-colors" onClick={() => setMobileMenuOpen(false)}>Tops & Bodysuits</Link>
                <Link to="/collections/sets" className="block text-gray-600 hover:text-pink-600 py-2 px-4 rounded-lg hover:bg-pink-50 transition-colors" onClick={() => setMobileMenuOpen(false)}>Sets & Loungewear</Link>
                <Link to="/collections/shorts" className="block text-gray-600 hover:text-pink-600 py-2 px-4 rounded-lg hover:bg-pink-50 transition-colors" onClick={() => setMobileMenuOpen(false)}>Shorts & Jeans</Link>
                <Link to="/collections/sunglasses" className="block text-gray-600 hover:text-pink-600 py-2 px-4 rounded-lg hover:bg-pink-50 transition-colors" onClick={() => setMobileMenuOpen(false)}>Sunglasses</Link>
                <Link to="/collections/jewelry" className="block text-gray-600 hover:text-pink-600 py-2 px-4 rounded-lg hover:bg-pink-50 transition-colors" onClick={() => setMobileMenuOpen(false)}>Jewelry</Link>
              </div>
              
              <div className="border-t border-pink-100 pt-4">
                <Link to="/about" className="block text-gray-700 hover:text-pink-600 font-medium py-3 px-4 rounded-xl hover:bg-pink-50 transition-colors" onClick={() => setMobileMenuOpen(false)}>About</Link>
                <Link to="/contact" className="block text-gray-700 hover:text-pink-600 font-medium py-3 px-4 rounded-xl hover:bg-pink-50 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
